/**
 * Per-item bridge affinity - the pure decision.
 *
 * Deliberately imports NOTHING. `bridgeAffinityMigration.ts` holds the sweep
 * that reaches the store; this holds the rule, so a smoke can drive it
 * directly. Same split as `ableton/deviceControl.ts`, and for the same
 * reason: pulling the layout store into a pure-logic test drags the whole
 * `sequencer`/`macros` chain with it, which logs `localStorage is not
 * defined` on import under Node. That noise is harmless and it is exactly
 * the kind of permanent output in which a real failure later goes unnoticed.
 *
 * # The defect this migration exists for
 *
 * `ItemBase.bridgeIds` carries bridge identifiers on 18 item types. Items
 * live on pages, pages on `Project`, and `layout/projectSync.ts` pushes the
 * whole `Project` as the document body - so every id in that array
 * replicates mesh-wide. `newBridgeId()` mints `br_` plus `Math.random()`
 * plus a timestamp, meaningful only on the machine that minted it, so on any
 * other machine the item resolved to nothing and read "sends nowhere".
 * `GuitarPedal.bridgeId` had the identical bug; this is the same rule
 * applied to its 18 other carriers.
 */

/** The minimum a caller must know about a bridge row to migrate against it. */
export interface AffinityRow {
  id: string;
  nodeId: string | null;
}

/**
 * The migrated `bridgeIds` for one item, or `null` when it needs no change.
 *
 * Rules, each load-bearing:
 *
 * - **`undefined` is LEFT ALONE.** It means "follow whichever bridge is
 *   default", which is dynamic and already correct on every machine.
 *   Replacing it with a concrete id would silently PIN the item to today's
 *   default - a behaviour change on every item the operator never
 *   configured, which is worse than the bug being fixed.
 * - **An id this machine cannot resolve is KEPT.** It is very likely another
 *   machine's row id and may resolve once that bridge is paired here.
 *   Dropping it would destroy the operator's intent with no way back.
 * - **A row with no `nodeId` yet keeps its row id.** The backfill lands on
 *   the first `/health`; the next sweep picks it up for free. Inventing an
 *   identity to avoid waiting would be strictly worse.
 */
export function migratedBridgeIds(
  ids: string[] | undefined,
  rowFor: (id: string) => AffinityRow | null,
): string[] | null {
  if (ids === undefined) return null;          // "default" - dynamic, leave it
  let changed = false;
  const next = ids.map((id) => {
    const row = rowFor(id);
    if (!row) return id;                       // unknown here - may resolve later
    if (!row.nodeId) return id;                // not backfilled yet
    if (id === row.nodeId) return id;          // already a node id
    changed = true;
    return row.nodeId;
  });
  return changed ? next : null;
}
